<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? `Editar parámetros ${form.year}` : "Nuevo año de parámetros" }}
      </h1>
    </div>

    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>

        <!-- Copiar del año anterior (solo en creación) -->
        <div v-if="!editing" class="mb-3">
          <button type="button" class="button button-light" @click="copyFromLatest">
            <i class="ri-file-copy-line mr-1"></i> Copiar valores del año anterior
          </button>
        </div>

        <!-- SECCIÓN 1 — Valores generales -->
        <b-card tag="article" no-body class="custom-card mb-3">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>Valores generales</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="year">Año <span class="text-danger">*</span></label>
                    <input
                      id="year"
                      type="number"
                      v-model.number="form.year"
                      placeholder="Ej: 2026"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.year.$error }"
                      :disabled="editing"
                    />
                    <FormError
                      v-if="$v.form.year.$error && !$v.form.year.required"
                      message="El año es requerido"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="uit">UIT en S/ <span class="text-danger">*</span></label>
                    <input
                      id="uit"
                      type="number"
                      v-model.number="form.uit"
                      step="0.01"
                      min="0"
                      placeholder="Ej: 5350.00"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.uit.$error }"
                    />
                    <FormError
                      v-if="$v.form.uit.$error && !$v.form.uit.required"
                      message="La UIT es requerida"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="rmv">RMV en S/ <span class="text-danger">*</span></label>
                    <input
                      id="rmv"
                      type="number"
                      v-model.number="form.rmv"
                      step="0.01"
                      min="0"
                      placeholder="Ej: 1130.00"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.rmv.$error }"
                    />
                    <FormError
                      v-if="$v.form.rmv.$error && !$v.form.rmv.required"
                      message="La RMV es requerida"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="familyAllowanceRate">Asignación familiar %</label>
                    <div class="input-group">
                      <input
                        id="familyAllowanceRate"
                        type="number"
                        v-model.number="form.familyAllowanceRate"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Ej: 10.00"
                        class="custom-form-control"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="essaludRate">Aporte EsSalud %</label>
                    <div class="input-group">
                      <input
                        id="essaludRate"
                        type="number"
                        v-model.number="form.essaludRate"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Ej: 9.00"
                        class="custom-form-control"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="fifthCategoryDeductionUits">Deducción 5ta cat. UITs</label>
                    <input
                      id="fifthCategoryDeductionUits"
                      type="number"
                      v-model.number="form.fifthCategoryDeductionUits"
                      min="0"
                      placeholder="Ej: 7"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="bonusGratificationRate">Bonif. extraordinaria grat. %</label>
                    <div class="input-group">
                      <input
                        id="bonusGratificationRate"
                        type="number"
                        v-model.number="form.bonusGratificationRate"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Ej: 9.00"
                        class="custom-form-control"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">%</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </section>
          </b-card-body>
        </b-card>

        <!-- SECCIÓN 2 — Escalas de IR 5ta categoría -->
        <b-card tag="article" no-body class="custom-card mb-3">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>Escalas de IR 5ta categoría</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <table class="custom-table w-100">
                <thead>
                  <tr>
                    <th style="width: 70px">TRAMO</th>
                    <th>DESDE (UITs)</th>
                    <th>HASTA (UITs)</th>
                    <th>TASA (%)</th>
                    <th style="width: 50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="brackets.length === 0">
                    <td colspan="5" class="text-center text-muted py-3">
                      No hay tramos. Agrega uno.
                    </td>
                  </tr>
                  <tr v-for="(b, idx) in brackets" :key="idx">
                    <td class="pl-3 text-center">{{ idx + 1 }}</td>
                    <td class="py-2 pr-2">
                      <input
                        type="number"
                        v-model.number="b.fromUits"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        class="custom-form-control"
                      />
                    </td>
                    <td class="py-2 pr-2">
                      <input
                        type="number"
                        v-model.number="b.toUits"
                        step="0.01"
                        min="0"
                        placeholder="Sin límite"
                        class="custom-form-control"
                      />
                    </td>
                    <td class="py-2 pr-2">
                      <div class="input-group">
                        <input
                          type="number"
                          v-model.number="b.rate"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="Ej: 8.00"
                          class="custom-form-control"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text">%</span>
                        </div>
                      </div>
                    </td>
                    <td class="text-center pr-2">
                      <button
                        type="button"
                        class="border-0 bg-transparent text-danger"
                        :disabled="brackets.length <= 1"
                        @click="removeBracket(idx)"
                      >
                        <i class="ri-close-line"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="p-3">
                <button type="button" class="button button-light" @click="addBracket">
                  <i class="ri-add-line mr-1"></i> Agregar tramo
                </button>
              </div>
            </section>
          </b-card-body>
        </b-card>

        <!-- SECCIÓN 3 — Días y beneficios -->
        <b-card tag="article" no-body class="custom-card mb-3">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>Días y beneficios</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="vacationDaysGeneral">Días vacaciones general</label>
                    <input
                      id="vacationDaysGeneral"
                      type="number"
                      v-model.number="form.vacationDaysGeneral"
                      min="0"
                      placeholder="30"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="vacationDaysMype">Días vacaciones MYPE</label>
                    <input
                      id="vacationDaysMype"
                      type="number"
                      v-model.number="form.vacationDaysMype"
                      min="0"
                      placeholder="15"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4"></div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="maternityDays">Días maternidad</label>
                    <input
                      id="maternityDays"
                      type="number"
                      v-model.number="form.maternityDays"
                      min="0"
                      placeholder="98"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="paternityDays">Días paternidad</label>
                    <input
                      id="paternityDays"
                      type="number"
                      v-model.number="form.paternityDays"
                      min="0"
                      placeholder="10"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="employerMedicalLeaveDays">Días DM empleador</label>
                    <input
                      id="employerMedicalLeaveDays"
                      type="number"
                      v-model.number="form.employerMedicalLeaveDays"
                      min="0"
                      placeholder="20"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="ctsDepositsPerYear">Depósitos CTS/año</label>
                    <input
                      id="ctsDepositsPerYear"
                      type="number"
                      v-model.number="form.ctsDepositsPerYear"
                      min="0"
                      placeholder="2"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="form-group">
                    <label for="gratificationsPerYear">Gratificaciones/año</label>
                    <input
                      id="gratificationsPerYear"
                      type="number"
                      v-model.number="form.gratificationsPerYear"
                      min="0"
                      placeholder="2"
                      class="custom-form-control"
                    />
                  </div>
                </div>

              </div>
            </section>
          </b-card-body>
        </b-card>

        <div class="form-group-buttons text-right">
          <router-link :to="{ name: 'legal-parameter-list' }" class="button button-light">
            {{ editing ? "Volver" : "Cancelar" }}
          </router-link>
          <button class="button button-primary" type="submit">Guardar</button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError } from "wize-admin";
import { required } from "vuelidate/lib/validators";
import { LegalParameterService } from "@/core/services";

export default {
  components: { FormError },

  data() {
    return {
      editing: false,
      form: {
        year:                       null,
        uit:                        null,
        rmv:                        null,
        familyAllowanceRate:        null,
        essaludRate:                null,
        fifthCategoryDeductionUits: null,
        bonusGratificationRate:     null,
        vacationDaysGeneral:        null,
        vacationDaysMype:           null,
        maternityDays:              null,
        paternityDays:              null,
        employerMedicalLeaveDays:   null,
        ctsDepositsPerYear:         null,
        gratificationsPerYear:      null,
      },
      brackets: [],
    };
  },

  validations() {
    return {
      form: {
        year: { required },
        uit:  { required },
        rmv:  { required },
      },
    };
  },

  methods: {
    addBracket() {
      this.brackets.push({ fromUits: null, toUits: null, rate: null });
    },

    removeBracket(idx) {
      if (this.brackets.length > 1) this.brackets.splice(idx, 1);
    },

    pctToDecimal(v) {
      return v != null && !isNaN(v) ? v / 100 : null;
    },

    decimalToPct(v) {
      return v != null ? parseFloat((v * 100).toFixed(4)) : null;
    },

    populateFormFromPayload(payload) {
      this.form = {
        year:                       payload.year,
        uit:                        payload.uit,
        rmv:                        payload.rmv,
        familyAllowanceRate:        this.decimalToPct(payload.familyAllowanceRate),
        essaludRate:                this.decimalToPct(payload.essaludRate),
        fifthCategoryDeductionUits: payload.fifthCategoryDeductionUits,
        bonusGratificationRate:     this.decimalToPct(payload.bonusGratificationRate),
        vacationDaysGeneral:        payload.vacationDaysGeneral,
        vacationDaysMype:           payload.vacationDaysMype,
        maternityDays:              payload.maternityDays,
        paternityDays:              payload.paternityDays,
        employerMedicalLeaveDays:   payload.employerMedicalLeaveDays,
        ctsDepositsPerYear:         payload.ctsDepositsPerYear,
        gratificationsPerYear:      payload.gratificationsPerYear,
      };
      this.brackets = (payload.brackets || []).map((b) => ({
        fromUits: b.fromUits,
        toUits:   b.toUits,
        rate:     this.decimalToPct(b.rate),
      }));
    },

    async copyFromLatest() {
      try {
        await this.$store.dispatch("app/loading", true);
        const payload = (await LegalParameterService.getLatest()).payload;
        const savedYear = this.form.year;
        this.populateFormFromPayload(payload);
        this.form.year = savedYear;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "No se pudo copiar el año anterior");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },

    async load(id) {
      try {
        await this.$store.dispatch("app/loading", true);
        const payload = (await LegalParameterService.get(id)).payload;
        this.populateFormFromPayload(payload);
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar los parámetros");
        this.$router.push({ name: "legal-parameter-list" });
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },

    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      try {
        await this.$store.dispatch("app/loading", true);

        const data = {
          year:                       this.form.year,
          uit:                        this.form.uit,
          rmv:                        this.form.rmv,
          familyAllowanceRate:        this.pctToDecimal(this.form.familyAllowanceRate),
          essaludRate:                this.pctToDecimal(this.form.essaludRate),
          fifthCategoryDeductionUits: this.form.fifthCategoryDeductionUits,
          bonusGratificationRate:     this.pctToDecimal(this.form.bonusGratificationRate),
          vacationDaysGeneral:        this.form.vacationDaysGeneral,
          vacationDaysMype:           this.form.vacationDaysMype,
          maternityDays:              this.form.maternityDays,
          paternityDays:              this.form.paternityDays,
          employerMedicalLeaveDays:   this.form.employerMedicalLeaveDays,
          ctsDepositsPerYear:         this.form.ctsDepositsPerYear,
          gratificationsPerYear:      this.form.gratificationsPerYear,
          brackets: this.brackets.map((b, i) => ({
            bracketOrder: i + 1,
            fromUits:     b.fromUits,
            toUits:       b.toUits != null && !isNaN(b.toUits) ? b.toUits : null,
            rate:         this.pctToDecimal(b.rate),
          })),
        };

        if (!this.editing) {
          await LegalParameterService.save(data);
        } else {
          await LegalParameterService.update(this.$route.params.id, data);
        }

        Alert.success("Se almacenó la información correctamente");
        this.$router.push({ name: "legal-parameter-list" });
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },

  async created() {
    if (this.$route.params.id) await this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
